import { useState, ChangeEvent, DragEvent, useEffect } from 'react';
import { campusMediaService } from 'services/api/campus-media.service';
import { sampleImages } from './sampleImages';
import { useEditedWidget } from 'features/Rooms/PersonalMeetingCorner/states/hooks';
import { UNSPLASH_ACCESS_KEY } from './unsplash';

export interface UploadedImage {
    id: string;
    file: File;
    previewUrl: string;
    description: string;
    width: number;
    height: number;
    author?: string;
    authorUrl?: string;
}

export interface UnsplashImage {
    id: string;
    url: string;
    description: string;
    width: number;
    height: number;
    author?: string;
    authorUrl?: string;
    downloadLocation?: string; // Add this field
}

interface UploadImageState {
    uploadedImage: UploadedImage | null;
    isDragging: boolean;
    searchQuery: string;
    unsplashImages: UnsplashImage[];
    setUploadedImage: (image: UploadedImage | null) => void;
    setIsDragging: (isDragging: boolean) => void;
    setSearchQuery: (query: string) => void;
    setUnsplashImages: (images: UnsplashImage[]) => void;
    selectedUnsplashImage: UnsplashImage | null;
    setSelectedUnsplashImage: (image: UnsplashImage | null) => void;
    loading: boolean;
    error: string | null;
    handleFile: (file: File) => void;
    onImageChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onDragOver: (e: DragEvent<HTMLDivElement>) => void;
    onDragLeave: (e: DragEvent<HTMLDivElement>) => void;
    onDrop: (e: DragEvent<HTMLDivElement>) => void;
    onDescriptionChange: (newValue?: string) => void;
    removeImage: () => void;
    handleUnsplashSearch: (newValue?: string) => Promise<void>;
    newAltOfImage: string;
    setNewAltOfImage: (alt: string) => void;
    preserveAspectRatio: boolean;
    setPreserveAspectRatio: (preserve: boolean) => void;
}

export const useUploadImageState = (onImageSelect: (content: any) => void): UploadImageState => {
    const [uploadedImage, setUploadedImage] = useState<UploadedImage | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    // const [unsplashImages, setUnsplashImages] = useState<UnsplashImage[]>([]);
    const [unsplashImages, setUnsplashImages] = useState<any[]>([]);
    const [selectedUnsplashImage, setSelectedUnsplashImage] = useState<UnsplashImage | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editedWidget] = useEditedWidget();
    const [newAltOfImage, setNewAltOfImage] = useState<string>('');
    const [preserveAspectRatio, setPreserveAspectRatio] = useState<boolean>(false);

    // Initialize preserve aspect ratio from edited widget if available
    useEffect(() => {
        if (editedWidget?.content?.aspectRatio) {
            setPreserveAspectRatio(editedWidget.content.aspectRatio === 'original');
        } else {
            // Reset to false for new widgets
            setPreserveAspectRatio(false);
        }
    }, [editedWidget]);

    const handleFile = async (file: File) => {
        // First get image dimensions
        const dimensions = await new Promise<{ width: number; height: number }>((resolve) => {
            const img = new Image();
            img.onload = () => {
                const maxWidth = 400;
                const aspectRatio = img.height / img.width;
                const height = maxWidth * aspectRatio;
                resolve({ width: maxWidth, height });
            };
            img.src = URL.createObjectURL(file);
        });

        // Then upload the file
        const result = await campusMediaService.uploadImage(file);
        const imageUrl = result.image_url;

        setUploadedImage({
            id: Math.random().toString(36).substr(2, 9),
            file,
            previewUrl: imageUrl,
            description: 'uploaded-image',
            width: dimensions.width,
            height: dimensions.height
        });
    };

    const onImageChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Check size (2MB limit)
        if (file.size > 2000000) {
            alert('File too large');
            return;
        }
        handleFile(file);
    };

    const onDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const onDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
    };

    const onDescriptionChange = (newValue?: string) => {
        if (uploadedImage) {
            setUploadedImage({ ...uploadedImage, description: newValue || '' });
        }
    };

    const removeImage = () => {
        if (uploadedImage) URL.revokeObjectURL(uploadedImage.previewUrl);
        setUploadedImage(null);
        setSelectedUnsplashImage(null);
    };

    const withUnsplashUtm = (url: string) => {
        const utm = 'utm_source=ivCAMPUS&utm_medium=referral';
        return url.includes('?') ? `${url}&${utm}` : `${url}?${utm}`;
    };

    const handleUnsplashSearch = async (newValue?: string) => {
        const query = newValue || '';
        setSearchQuery(query);
        setSelectedUnsplashImage(null);
        if (!query.trim()) return setUnsplashImages([]);

        setLoading(true);
        setError(null);
        try {
            if (!UNSPLASH_ACCESS_KEY) {
                throw new Error('Using demo fallback images');
            }

            const res = await fetch(`https://api.unsplash.com/search/photos?query=${encodeURIComponent(query)}&per_page=12&client_id=${UNSPLASH_ACCESS_KEY}`);
            if (!res.ok) throw new Error('Fetch failed');
            const data = await res.json();

            const images = data.results.map((img: any) => {
                const maxWidth = 400;
                const aspectRatio = img.height / img.width;
                const height = maxWidth * aspectRatio;

                return {
                    id: img.id,
                    url: withUnsplashUtm(img.urls.small),
                    description: img.alt_description || '',
                    width: maxWidth,
                    height,
                    author: img.user.name,
                    authorUrl: withUnsplashUtm(img.user.links.html),
                    downloadLocation: img.links.download_location
                };
            });

            setUnsplashImages(images);
        } catch (error) {
            console.error('Showing demo images. Get a free Unsplash API key at unsplash.com/developers.', error);

            // Transform sampleImages to match the expected structure
            const transformedSampleImages = sampleImages.map((img) => {
                const maxWidth = 400;
                const aspectRatio = 0.75; // Default aspect ratio for sample images
                const height = maxWidth * aspectRatio;

                return {
                    id: img.id,
                    url: img.urls.regular,
                    description: img.description,
                    width: maxWidth,
                    height,
                    author: img.user.name,
                    authorUrl: img.user.links.html,
                    downloadLocation: img.links.download_location
                };
            });

            setUnsplashImages(transformedSampleImages);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Use consistent as default, original when preserveAspectRatio is true
        const finalAspectRatio = preserveAspectRatio ? 'original' : 'consistent';

        if (uploadedImage) {
            onImageSelect({
                imageUrl: uploadedImage.previewUrl,
                alt: uploadedImage.description,
                width: uploadedImage.width,
                height: uploadedImage.height,
                author: uploadedImage.author || '',
                authorUrl: uploadedImage.authorUrl || '',
                aspectRatio: finalAspectRatio
            });
        } else if (selectedUnsplashImage) {
            onImageSelect({
                imageUrl: selectedUnsplashImage.url,
                alt: selectedUnsplashImage.description,
                width: selectedUnsplashImage.width,
                height: selectedUnsplashImage.height,
                author: selectedUnsplashImage.author || '',
                authorUrl: selectedUnsplashImage.authorUrl || '',
                downloadLocation: selectedUnsplashImage.downloadLocation || '',
                aspectRatio: finalAspectRatio
            });
        } else if (editedWidget?.content) {
            onImageSelect({
                imageUrl: editedWidget.content.imageUrl,
                alt: newAltOfImage ? newAltOfImage : editedWidget.content.alt,
                width: editedWidget.content.width,
                height: editedWidget.content.height,
                author: editedWidget.content.author || '',
                authorUrl: editedWidget.content.authorUrl || '',
                downloadLocation: editedWidget.content.downloadLocation || '',
                aspectRatio: finalAspectRatio
            });
        } else {
            onImageSelect({});
        }
    }, [uploadedImage, selectedUnsplashImage, editedWidget?.content, newAltOfImage, preserveAspectRatio]);

    return {
        uploadedImage,
        setUploadedImage,
        isDragging,
        setIsDragging,
        searchQuery,
        setSearchQuery,
        unsplashImages,
        setUnsplashImages,
        selectedUnsplashImage,
        setSelectedUnsplashImage,
        loading,
        error,
        handleFile,
        onImageChange,
        onDragOver,
        onDragLeave,
        onDrop,
        onDescriptionChange,
        removeImage,
        handleUnsplashSearch,
        newAltOfImage,
        setNewAltOfImage,
        preserveAspectRatio,
        setPreserveAspectRatio
    };
};
